import { Helmet } from 'react-helmet-async';
import { AgGridReact } from 'ag-grid-react';
import { useState, useEffect, useRef } from 'react';
import { enqueueSnackbar } from 'notistack';
import { Box, Button, CircularProgress, IconButton, Stack, Tooltip, Typography , } from '@mui/material';
import BuildIcon from '@mui/icons-material/Build';
import axiosInstance from '../../utils/axios';
import EditIconButton from '../../reuseable-components/DefaultButtons/EditIconButton';
import DeleteIconButton from '../../reuseable-components/DefaultButtons/DeleteIconButton';
import OrderGuideForm from './OrderGuideForm';
import OrderGuideUpdateByPercenatge from './OrderGuideUpdateByPercenatge';
import OrderGuideProductsForm from './OrderGuideProductsForm';
import Iconify from '../../reuseable-components/iconify/Iconify';
import AgGridDefaults from '../../reuseable-components/AGGrid/AGGridDefaults';
import AGGridQuickFilterWrapper from '../../reuseable-components/AGGrid/AGGridQuickFilterWrapper';

export default function OrderGuideList() {
  const gridRef = useRef(null);

  const [orderGuides, setOrderGuides] = useState([]);
  const [purchasingGroups, setPurchasingGroups] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [productCategories, setProductCategories] = useState([]);
  const [editOrderGuide, setEditOrderGuide] = useState(null);
  const [openOrderGuideForm, setOpenOrderGuideForm] = useState(false);
  const [products, setProducts] = useState([]);
  const [openOrderGuideProductsForm, setOpenOrderGuideProductsForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [openOrderGuideUpdateByPercenatge, setOpenOrderGuideUpdateByPercenatge] = useState(false);

  useEffect(() => {
    setLoading(true);
    axiosInstance.get("/orderGuide/orderGuides")
      .then(res => {
        setOrderGuides(res.data.orderGuides.map(orderGuide => ({
          ...orderGuide,
          facilityName: orderGuide.facilityId > 0 ? res.data.facilities.find(fac => fac.value === orderGuide.facilityId)?.label : "",
          purchasingGroupName: res.data.purchasingGroups.find(group => group.value === orderGuide.purchasingGroupId)?.label,
          productCategoryName: res.data.productCategories.find(group => group.value === orderGuide.productCategoryId)?.label,
        })));
        setFacilities(res.data.facilities);
        setPurchasingGroups(res.data.purchasingGroups);
        setProductCategories(res.data.productCategories);
      })
      .catch(() => enqueueSnackbar('Order guide list failed', { variant: 'error' }))
      .finally(() => setLoading(false));

    axiosInstance.get("/orderGuide/products")
      .then(res => setProducts((res.data ?? []).flatMap(product => product?.uomTypes.map(uomType => ({
        ...uomType,
        name: product.name,
        vendorItemId: product.vendorItemId,
        productCategoryId: product.productCategoryId,
        productId: product.id
      })))))
      .catch(() => enqueueSnackbar('Order guide product list failed', { variant: 'error' }))
      .finally(() => setLoadingProducts(false));
  }, []);

  const deleteOrderGuide = (id) => {
    axiosInstance.post(`/orderGuide/deleteOrderGuide?id=${id}`)
      .then(() => {
        setOrderGuides(orderGuides => [...orderGuides.filter(orderGuide => orderGuide.id !== id)]);
        enqueueSnackbar('Order guide deleted', { variant: 'success' })
      })
      .catch(() => enqueueSnackbar('Order guide delete failed', { variant: 'error' }))
  };

  const columns = [
    {
      field: 'purchasingGroupName',
      headerName: 'Purchasing Group',
      minWidth: 150,
      flex: 1
    },
    {
      field: 'facilityName',
      headerName: 'Facility',
      minWidth: 100,
      flex: 1
    },
    {
      field: 'productCategoryName',
      headerName: 'Category',
      minWidth: 100,
      flex: 1
    },
    {
      field: 'itemCount',
      width: 105,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      cellRenderer: (params) => <Stack direction="row" spacing={1}>
        <Tooltip title={"Configure order guide items"} arrow placement="top">
          <IconButton edge="end" color="secondary" onClick={() => { setEditOrderGuide(params.data); setOpenOrderGuideProductsForm(true); }} disableRipple sx={{ px: 0 }}>
            <BuildIcon />
          </IconButton>
        </Tooltip>
        <EditIconButton onClick={() => { setEditOrderGuide(params.data); setOpenOrderGuideForm(true); }} />
        <DeleteIconButton content={`Are you sure you want to delete ${params.data.purchasingGroupName}${params.data.facilityName ? ` | ${params.data.facilityName}`
          : ""} | ${params.data.productCategoryName}?`} action={() => deleteOrderGuide(params.data.id)} />
      </Stack>
    }
  ];

  return <>
    <Helmet>
      <title>Order Guide List | NHIDirect</title>
    </Helmet>

    <Box sx={{ height: "calc(100% - 55px)", mt: 2 }}>
      <AGGridQuickFilterWrapper gridRef={gridRef}>
        <Button variant="contained" startIcon={<Iconify icon="eva:plus-fill" />} onClick={() => { setEditOrderGuide(null); setOpenOrderGuideForm(true); }} color="primary" size="small">
          <Typography>Add Order Guide</Typography>
        </Button>
        <Button variant="contained" startIcon={<Iconify icon="tabler:percentage" />} onClick={() => { setOpenOrderGuideUpdateByPercenatge(true); }} color="primary" size="small">
          <Typography>Pricing Update</Typography>
        </Button>
      </AGGridQuickFilterWrapper>
      {!loading ? <Box sx={{ height: "max(500px, 100%)" }}>
        <AgGridReact ref={gridRef} {...AgGridDefaults} columnDefs={columns} rowData={orderGuides} loading={loading} />
      </Box> : <Box sx={{ display: "flex", justifyContent: "center", m: 3 }}><CircularProgress /></Box>}
      <OrderGuideForm editOrderGuide={editOrderGuide} open={openOrderGuideForm} close={() => { setEditOrderGuide(null); setOpenOrderGuideForm(false); }}
        purchasingGroups={purchasingGroups} productCategories={productCategories} facilities={facilities} setOrderGuides={setOrderGuides} />
      <OrderGuideProductsForm
        editOrderGuide={editOrderGuide}
        open={openOrderGuideProductsForm}
        close={() => { setEditOrderGuide(null); setOpenOrderGuideProductsForm(false); }}
        setOrderGuides={setOrderGuides}
        products={products}
        purchasingGroups={purchasingGroups}
        productCategories={productCategories}
        facilities={facilities}
        loadingProducts={loadingProducts}
      />
      <OrderGuideUpdateByPercenatge
        open={openOrderGuideUpdateByPercenatge}
        close={() => { setOpenOrderGuideUpdateByPercenatge(false); }}
        products={products}
        purchasingGroups={purchasingGroups}
        productCategories={productCategories}
        facilities={facilities}
        loadingProducts={loadingProducts}
      />
    </Box>
  </>;
}

